/**
 * Lớp gọi API nội bộ, sinh kiểu từ @htttn/contracts (tức từ OpenAPI).
 *
 * Không thành phần nào được gọi fetch trực tiếp; mọi lời gọi đi qua `khachApi`
 * để có sẵn phong bì phản hồi, token và xử lý mã lỗi thống nhất.
 */
import createClient, { type Middleware } from 'openapi-fetch';
import type { paths } from '@htttn/contracts';

const KHOA_TOKEN = 'htttn.accessToken';

export function layToken(): string | null {
  try {
    return globalThis.localStorage?.getItem(KHOA_TOKEN) ?? null;
  } catch {
    return null;
  }
}

export function datToken(token: string | null): void {
  try {
    if (token === null) globalThis.localStorage?.removeItem(KHOA_TOKEN);
    else globalThis.localStorage?.setItem(KHOA_TOKEN, token);
  } catch {
    // Trình duyệt chặn localStorage: bỏ qua, người dùng sẽ phải đăng nhập lại.
  }
}

/** Gắn token người dùng vào mọi lời gọi. */
const ganToken: Middleware = {
  async onRequest({ request }) {
    const token = layToken();
    if (token) request.headers.set('Authorization', `Bearer ${token}`);
    return request;
  },
};

export const khachApi = createClient<paths>({
  baseUrl: (import.meta.env?.VITE_API_BASE_URL as string | undefined) ?? '/',
});

khachApi.use(ganToken);

/** Phong bì phản hồi của API nội bộ, khớp schema PhanHoiNoiBo trong OpenAPI. */
export interface PhongBiNoiBo<T> {
  success: boolean;
  code: string;
  message: string;
  data: T | null;
  requestId: string;
}

/** Hồ sơ người dùng hiện tại do GET /api/v1/auth/me trả về. */
export interface HoSoNguoiDung {
  id: string;
  tenDangNhap: string;
  hoTen: string;
  email: string | null;
  tenantId: string | null;
  maTinh: string | null;
  tenTinh: string | null;
  laQuanTriNenTang: boolean;
  quyen: string[];
  phamViDiaBan: string[];
  nhom: string[];
  /** Cấp tài khoản (hợp đồng A0 tài khoản xã mục 3). Vắng khi API cũ: coi là 'tinh'. */
  cap?: 'tinh' | 'xa';
  /** Tài khoản đang chờ đổi mật khẩu lần đầu. */
  phaiDoiMatKhau?: boolean;
  /** Tên đơn vị hành chính tương ứng `phamViDiaBan`, cùng thứ tự. */
  tenPhamVi?: string[];
  /** Hợp đồng A0 hồ sơ – thông báo mục 4; vắng khi API cũ. */
  dienThoai?: string | null;
  diaChi?: string | null;
}

/**
 * Dữ liệu đăng nhập — chung cho POST /auth/login, /auth/2fa/xac-thuc và
 * /auth/doi-mat-khau-lan-dau. Chỉ có token khi không còn bước nào phải làm.
 */
export interface KetQuaDangNhap {
  canXacThucHaiLop: boolean;
  /** Vắng ở API cũ: coi là false. */
  canDoiMatKhau?: boolean;
  phienTam: string | null;
  accessToken: string | null;
  refreshToken?: string | null;
  expiresIn: number;
}

/**
 * Gọi POST /api/v1/auth/login. Dùng fetch trực tiếp qua khachApi để giữ một chỗ
 * duy nhất đọc biến môi trường và xử lý phong bì.
 */
export async function dangNhap(tenDangNhap: string, matKhau: string): Promise<PhongBiNoiBo<KetQuaDangNhap>> {
  const { data, error } = await khachApi.POST('/api/v1/auth/login', {
    body: { tenDangNhap, matKhau },
  });
  const phanHoi = (data ?? error) as PhongBiNoiBo<KetQuaDangNhap>;
  if (phanHoi?.success && phanHoi.data?.accessToken) datToken(phanHoi.data.accessToken);
  return phanHoi;
}

/** Gọi một điểm cuối công khai của bước đăng nhập (chưa có trong OpenAPI) bằng fetch thô. */
async function guiBuocDangNhap(duongDan: string, than: unknown): Promise<PhongBiNoiBo<KetQuaDangNhap>> {
  const goc = (import.meta.env?.VITE_API_BASE_URL as string | undefined) ?? '';
  const res = await fetch(`${goc}${duongDan}`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(than),
  });
  const phanHoi = (await res.json()) as PhongBiNoiBo<KetQuaDangNhap>;
  if (phanHoi?.success && phanHoi.data?.accessToken) datToken(phanHoi.data.accessToken);
  return phanHoi;
}

/**
 * POST /api/v1/auth/doi-mat-khau-lan-dau — đổi mật khẩu bắt buộc ở lần đăng nhập
 * đầu bằng phiên tạm; phản hồi cùng dạng đăng nhập (có thể còn bước hai lớp).
 */
export function doiMatKhauLanDau(
  phienTam: string,
  matKhauMoi: string,
): Promise<PhongBiNoiBo<KetQuaDangNhap>> {
  return guiBuocDangNhap('/api/v1/auth/doi-mat-khau-lan-dau', { phienTam, matKhauMoi });
}

/** POST /api/v1/auth/2fa/xac-thuc — đổi phiên tạm + mã hai lớp lấy token. */
export function xacThucHaiLop(phienTam: string, ma: string): Promise<PhongBiNoiBo<KetQuaDangNhap>> {
  return guiBuocDangNhap('/api/v1/auth/2fa/xac-thuc', { phienTam, ma });
}

/**
 * Gọi GET /api/v1/auth/me. Đường dẫn này do đợt 0 bổ sung, chưa có trong
 * OpenAPI 1.0.0, nên gọi bằng fetch thô (xem NOTES-A0.md).
 */
export async function hoSoHienTai(): Promise<PhongBiNoiBo<HoSoNguoiDung>> {
  const goc = (import.meta.env?.VITE_API_BASE_URL as string | undefined) ?? '';
  const token = layToken();
  const res = await fetch(`${goc}/api/v1/auth/me`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  return (await res.json()) as PhongBiNoiBo<HoSoNguoiDung>;
}
